import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, PencilLine, Save } from "lucide-react";
import Filosofia, { EMPTY_FILOSOFIA } from "@/components/Filosofia";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
    "w-full border border-white/15 bg-navy-deep px-4 py-3 text-sm leading-relaxed text-[#F8F9FA] focus:border-gold focus:outline-none transition-colors";
const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF]";

export default function ContentEditor({ token }) {
    const [draft, setDraft] = useState(EMPTY_FILOSOFIA);
    const [published, setPublished] = useState(null);
    const [preview, setPreview] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios.get(`${API}/site-content/filosofia`).then((r) => {
            setDraft(r.data);
            setPublished(r.data);
        });
    }, []);

    const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
    const setPrincipio = (i, k) => (e) => {
        const principios = draft.principios.map((p, idx) => (idx === i ? { ...p, [k]: e.target.value } : p));
        setDraft({ ...draft, principios });
    };
    const dirty = JSON.stringify(draft) !== JSON.stringify(published);

    const publicar = async () => {
        setSaving(true);
        try {
            const { updated_at, ...body } = draft;
            const { data } = await axios.put(`${API}/site-content/filosofia`, body, { headers: { Authorization: `Bearer ${token}` } });
            setDraft(data);
            setPublished(data);
            toast.success("Contenido publicado", { description: "Los cambios ya están visibles en la página." });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("No se pudo publicar", { description: Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : detail || "Revisa los campos." });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mx-auto mt-8 max-w-[1440px]" data-testid="content-editor">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
                <div>
                    <h2 className="font-display text-2xl font-medium tracking-tight">Misión y Visión</h2>
                    <p className="mt-1 text-sm text-[#9CA3AF]" data-testid="content-status">
                        {dirty ? "Cambios sin publicar" : "Todo publicado"}
                    </p>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => setPreview(!preview)} className="flex items-center gap-2 border border-white/15 px-4 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors hover:border-gold" data-testid="content-preview-toggle">
                        {preview ? <PencilLine size={14} /> : <Eye size={14} />} {preview ? "Volver a editar" : "Vista previa"}
                    </button>
                    <button onClick={publicar} disabled={!dirty || saving} className="flex items-center gap-2 bg-gold px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy transition-colors hover:bg-gold-soft disabled:opacity-50" data-testid="content-publish-button">
                        <Save size={14} /> {saving ? "Publicando…" : "Publicar"}
                    </button>
                </div>
            </div>

            {preview ? (
                <div className="mt-8 overflow-hidden rounded border border-gold/40" data-testid="content-preview">
                    <p className="bg-gold px-4 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.25em] text-navy">Vista previa — así se verá en la página</p>
                    <Filosofia content={draft} preview />
                </div>
            ) : (
                <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2" data-testid="content-form">
                    <div>
                        <label htmlFor="mision" className={labelCls}>Misión</label>
                        <textarea id="mision" rows={10} value={draft.mision} onChange={set("mision")} className={inputCls} data-testid="content-mision-input" />
                    </div>
                    <div>
                        <label htmlFor="vision" className={labelCls}>Visión</label>
                        <textarea id="vision" rows={10} value={draft.vision} onChange={set("vision")} className={inputCls} data-testid="content-vision-input" />
                    </div>
                    <div className="lg:col-span-2">
                        <p className={labelCls}>Nuestros principios</p>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            {draft.principios.map((p, i) => (
                                <div key={i} className="space-y-2 border border-white/10 p-4">
                                    <input value={p.nombre} onChange={setPrincipio(i, "nombre")} placeholder={`Principio ${i + 1}`} className={inputCls} data-testid={`content-principio-nombre-${i}`} />
                                    <input value={p.frase} onChange={setPrincipio(i, "frase")} placeholder="Frase corta" className={inputCls} data-testid={`content-principio-frase-${i}`} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label htmlFor="cita" className={labelCls}>Cita destacada</label>
                        <textarea id="cita" rows={3} value={draft.cita} onChange={set("cita")} className={inputCls} data-testid="content-cita-input" />
                    </div>
                    <div>
                        <label htmlFor="cita_autor" className={labelCls}>Autor de la cita</label>
                        <input id="cita_autor" value={draft.cita_autor} onChange={set("cita_autor")} className={inputCls} data-testid="content-cita-autor-input" />
                    </div>
                </div>
            )}
        </div>
    );
}
